import { createCard } from './create-card';
import { initProductCardGalleries } from './product-card-gallery';
import { postWcAjax, replaceWooFragments } from './cart-ajax-client';

class ProductsListing {
    constructor() {
        this.root = document.querySelector('[data-products-listing]');
        if (!this.root) return;

        this.storageKey = 'sp-products-listing-view';
        this.addToCartEndpoint = 'sp_add_to_cart';
        this.cartQuantitiesEndpoint = 'sp_cart_product_quantities';
        this.activePanel = null;
        this.abortController = null;
        this.cartCountsRefreshTimer = null;
        this.cartCountsRequestId = 0;
        this.skipNextCartUpdated = false;
        this.isLoading = false;

        this.onEsc = this.onEsc.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onRootClick = this.onRootClick.bind(this);
        this.onRootSubmit = this.onRootSubmit.bind(this);
        this.onRootChange = this.onRootChange.bind(this);
        this.onRootKeydown = this.onRootKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onCartUpdated = this.onCartUpdated.bind(this);
    }

    /**
     * Uruchamia wszystkie handlery dla listingu produktów.
     */
    init() {
        if (!(this.root instanceof HTMLElement)) {
            return;
        }

        this.refreshUiRefs();
        this.bindDelegatedEvents();
        this.applySavedView();
        this.initAccordions();
        this.initPriceRanges();
        this.initProductCartCounts();
    }

    /**
     * Odświeża referencje do paneli i overlayu po podmianie HTML.
     */
    refreshUiRefs() {
        if (!(this.root instanceof HTMLElement)) {
            return;
        }

        this.overlay = this.root.querySelector('[data-products-overlay]');
        this.filtersPanel = this.root.querySelector('[data-products-filters-panel]');
        this.sortPanel = this.root.querySelector('[data-products-sort-panel]');
        this.grid = this.root.querySelector('[data-products-grid]');
        this.viewDropdown = this.root.querySelector('[data-products-view-dropdown]');
    }

    /**
     * Inicjalizuje badge z ilością danego produktu w koszyku.
     */
    initProductCartCounts() {
        this.scheduleProductCartCountsRefresh(0);
    }

    /**
     * Podpina eventy dla kliknięć, submitów i zmian w obrębie listingu.
     */
    bindDelegatedEvents() {
        this.root.addEventListener('click', this.onRootClick);
        this.root.addEventListener('submit', this.onRootSubmit);
        this.root.addEventListener('change', this.onRootChange);
        this.root.addEventListener('keydown', this.onRootKeydown);

        document.addEventListener('keydown', this.onEsc);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('popstate', this.onPopState);

        if (window.jQuery) {
            window.jQuery(document.body).on(
                'added_to_cart removed_from_cart wc_fragments_refreshed',
                this.onCartUpdated
            );
        }
    }

    /**
     * Po zmianie koszyka odświeża liczniki na listingu.
     */
    onCartUpdated() {
        if (this.skipNextCartUpdated) {
            this.skipNextCartUpdated = false;
            return;
        }

        this.scheduleProductCartCountsRefresh(120);
    }

    /**
     * Zwraca true dla prostych produktów obsługiwanych AJAX-em.
     */
    isAjaxAddToCartButton(button) {
        return (
            button instanceof HTMLElement &&
            button.hasAttribute('data-product-id') &&
            button.getAttribute('aria-disabled') !== 'true'
        );
    }

    /**
     * Zwraca unikalne ID produktów widocznych na aktualnym listingu.
     */
    getListingProductIds() {
        const ids = new Set();

        this.root
            .querySelectorAll('[data-products-list-add-to-cart][data-product-id]')
            .forEach(button => {
                const productId = parseInt(
                    button.getAttribute('data-product-id') || '0',
                    10
                );

                if (productId > 0) {
                    ids.add(productId);
                }
            });

        return Array.from(ids);
    }

    /**
     * Pobiera ilości z koszyka i aktualizuje badge przy przyciskach.
     */
    scheduleProductCartCountsRefresh(delay = 120) {
        if (this.cartCountsRefreshTimer) {
            window.clearTimeout(this.cartCountsRefreshTimer);
        }

        this.cartCountsRefreshTimer = window.setTimeout(() => {
            this.refreshProductCartCounts();
        }, delay);
    }

    /**
     * Pobiera ilości z koszyka i aktualizuje badge przy przyciskach.
     */
    refreshProductCartCounts() {
        const productIds = this.getListingProductIds();
        if (!productIds.length) {
            return;
        }

        const data = new URLSearchParams();
        productIds.forEach(productId => {
            data.append('product_ids[]', String(productId));
        });

        const requestId = ++this.cartCountsRequestId;

        postWcAjax(this.cartQuantitiesEndpoint, data)
            .then(response => {
                if (requestId !== this.cartCountsRequestId) {
                    return;
                }

                if (!response?.success) {
                    return;
                }

                this.updateProductCartCounts(
                    response.data?.product_quantities || {}
                );
            })
            .catch(() => {});
    }

    /**
     * Ustawia stan badge na podstawie mapy product_id => qty.
     */
    updateProductCartCounts(quantities = {}) {
        this.root
            .querySelectorAll('[data-products-list-add-to-cart][data-product-id]')
            .forEach(button => {
                const productId = button.getAttribute('data-product-id') || '';
                const badge = button.querySelector('[data-product-cart-count]');
                if (!(badge instanceof HTMLElement)) {
                    return;
                }

                const quantity = parseInt(quantities[productId] || '0', 10) || 0;
                badge.textContent = String(quantity);
                badge.hidden = quantity < 1;
                badge.setAttribute(
                    'aria-hidden',
                    quantity > 0 ? 'false' : 'true'
                );
            });
    }

    /**
     * Ustawia badge tylko dla produktów obecnych w mapie quantities.
     */
    updateProductCartCountsPartial(quantities = {}) {
        Object.entries(quantities || {}).forEach(([productId, rawQuantity]) => {
            if (!productId) {
                return;
            }

            this.root
                .querySelectorAll(
                    `[data-products-list-add-to-cart][data-product-id="${productId}"]`
                )
                .forEach(button => {
                    const badge = button.querySelector('[data-product-cart-count]');
                    if (!(badge instanceof HTMLElement)) {
                        return;
                    }

                    const quantity = parseInt(rawQuantity || '0', 10) || 0;
                    badge.textContent = String(quantity);
                    badge.hidden = quantity < 1;
                    badge.setAttribute(
                        'aria-hidden',
                        quantity > 0 ? 'false' : 'true'
                    );
                });
        });
    }

    /**
     * Buduje payload add-to-cart dla przycisku listingu.
     */
    buildAddToCartPayload(button) {
        const productId = button.getAttribute('data-product-id') || '';
        if (!productId) {
            return null;
        }

        const quantity = button.getAttribute('data-quantity') || '1';
        const payload = new URLSearchParams();
        payload.append('product_id', productId);
        payload.append('qty', quantity);

        return {
            productId,
            payload,
        };
    }

    /**
     * Przełącza stan loading na przycisku add-to-cart.
     */
    setAddToCartLoadingState(button, loading) {
        if (!(button instanceof HTMLElement)) {
            return;
        }

        if (loading) {
            button.dataset.spLoading = '1';
            button.classList.add('loading');
            button.setAttribute('aria-busy', 'true');
            return;
        }

        delete button.dataset.spLoading;
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
    }

    /**
     * Zwraca mapę ilości produktów z odpowiedzi add-to-cart.
     */
    getProductQuantitiesFromResponse(data, productId) {
        if (data?.product_quantities) {
            return data.product_quantities;
        }

        if (typeof data?.product_quantity !== 'undefined') {
            return {
                [productId]: data.product_quantity,
            };
        }

        return {};
    }

    /**
     * Obsługuje sukces add-to-cart.
     */
    handleAddToCartSuccess(button, productId, data = {}) {
        if (data.fragments) {
            this.replaceFragments(data.fragments);
        }

        const quantities = this.getProductQuantitiesFromResponse(
            data,
            productId
        );
        if (Object.keys(quantities).length) {
            this.updateProductCartCountsPartial(quantities);
        }

        if (window.jQuery) {
            this.skipNextCartUpdated = true;
            window.jQuery(document.body).trigger('added_to_cart', [
                data.fragments || {},
                data.cart_hash || '',
                window.jQuery(button),
            ]);
        }
    }

    /**
     * Obsługuje błąd add-to-cart.
     */
    handleAddToCartError(response) {
        const errorHtml = this.extractErrorHtml(response?.data?.errors);
        const message =
            response?.data?.message ||
            this.extractErrorMessage(response?.data?.errors) ||
            'Nie można dodać produktu do koszyka.';

        this.showCartToast(errorHtml || message, {
            html: Boolean(errorHtml),
        });
    }

    /**
     * Obsługuje listingowy add-to-cart bez przeładowania strony.
     */
    handleAjaxAddToCart(button) {
        if (!(button instanceof HTMLElement) || button.dataset.spLoading === '1') {
            return;
        }

        const request = this.buildAddToCartPayload(button);
        if (!request) {
            return;
        }

        this.setAddToCartLoadingState(button, true);

        postWcAjax(this.addToCartEndpoint, request.payload)
            .then(response => {
                if (!response?.success) {
                    this.handleAddToCartError(response);
                    return;
                }

                this.handleAddToCartSuccess(
                    button,
                    request.productId,
                    response.data || {}
                );
            })
            .catch(() => {
                this.showCartToast(
                    'Wystąpił błąd podczas dodawania do koszyka.'
                );
            })
            .finally(() => {
                this.setAddToCartLoadingState(button, false);
            });
    }

    /**
     * Podmienia fragmenty WooCommerce zwrócone przez AJAX.
     */
    replaceFragments(fragments) {
        replaceWooFragments(fragments);
    }

    /**
     * Wyciąga czytelny komunikat z listy błędów WooCommerce.
     */
    extractErrorMessage(errors) {
        if (!Array.isArray(errors) || !errors.length) {
            return '';
        }

        return this.decodeHtml(
            errors
            .map(error => (error?.notice ? String(error.notice) : ''))
            .join(' ')
            .replace(/<[^>]*>/g, '')
            .trim()
        );
    }

    /**
     * Zwraca HTML notice WooCommerce, aby linki pozostały klikalne.
     */
    extractErrorHtml(errors) {
        if (!Array.isArray(errors) || !errors.length) {
            return '';
        }

        const html = errors
            .map(error => (error?.notice ? String(error.notice) : ''))
            .join(' ')
            .trim();

        return html || '';
    }

    /**
     * Dekoduje encje HTML w komunikatach z backendu.
     */
    decodeHtml(text) {
        const temp = document.createElement('div');
        temp.innerHTML = text || '';
        return temp.textContent || '';
    }

    /**
     * Pokazuje komunikat w toascie koszyka.
     */
    showCartToast(message, options = {}) {
        if (!message) {
            return;
        }

        document.body.dispatchEvent(
            new CustomEvent('sp:toast', {
                detail: {
                    id: 'cart',
                    text: message,
                    html: Boolean(options.html),
                },
            })
        );
    }

    /**
     * Obsługuje kliknięcia przycisków paneli i linków paginacji/filtrów.
     */
    onRootClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        if (target.closest('[data-products-overlay]')) {
            event.preventDefault();
            this.closePanels();
            return;
        }

        if (target.closest('[data-products-open-filters]')) {
            event.preventDefault();
            this.openPanel('filters');
            return;
        }

        if (target.closest('[data-products-close-filters]')) {
            event.preventDefault();
            this.closePanels();
            return;
        }

        if (target.closest('[data-products-open-sort]')) {
            event.preventDefault();
            this.openPanel('sort');
            return;
        }

        if (target.closest('[data-products-close-sort]')) {
            event.preventDefault();
            this.closePanels();
            return;
        }

        if (target.closest('[data-products-sort-toggle]')) {
            event.preventDefault();
            this.toggleSortDropdown();
            return;
        }

        if (target.closest('[data-products-view-toggle]')) {
            event.preventDefault();
            this.toggleViewDropdown();
            return;
        }

        const viewOption = target.closest('[data-products-view-option]');
        if (viewOption) {
            event.preventDefault();
            this.selectViewOption(viewOption);
            return;
        }

        const qtyStepButton = target.closest('[data-products-list-qty-step]');
        if (qtyStepButton) {
            event.preventDefault();
            this.handleQtyStep(qtyStepButton);
            return;
        }

        const addToCartButton = target.closest('[data-products-list-add-to-cart]');
        if (addToCartButton) {
            event.preventDefault();
            event.stopImmediatePropagation();

            const isDisabled =
                addToCartButton.getAttribute('aria-disabled') === 'true' ||
                addToCartButton.classList.contains('is-disabled');
            if (isDisabled) {
                return;
            }

            if (this.isAjaxAddToCartButton(addToCartButton)) {
                this.syncAddToCartQuantity(addToCartButton);
                this.handleAjaxAddToCart(addToCartButton);
                return;
            }

            this.syncAddToCartQuantity(addToCartButton);
            const fallbackUrl =
                addToCartButton.getAttribute('data-add-to-cart-url') ||
                addToCartButton.getAttribute('href') ||
                '';

            if (fallbackUrl && fallbackUrl !== '#') {
                window.location.href = fallbackUrl;
            }
            return;
        }

        const accordionSummary = target.closest('[data-filter-accordion-summary]');
        if (accordionSummary) {
            event.preventDefault();
            const accordion = accordionSummary.closest('[data-filter-accordion]');
            this.toggleAccordion(accordion);
            return;
        }

        const sortOption = target.closest('[data-products-sort-option]');
        if (sortOption) {
            event.preventDefault();
            this.selectSortOption(sortOption);
            return;
        }

        const link = target.closest(
            '[data-products-pagination] a, [data-products-chips] a, [data-products-filters-form] a'
        );
        if (!link || !(link instanceof HTMLAnchorElement)) {
            return;
        }

        if (!link.href || !this.isSameOrigin(link.href)) {
            return;
        }

        event.preventDefault();
        this.navigate(link.href);
    }

    /**
     * Obsługuje submit formularzy listingu przez AJAX.
     */
    onRootSubmit(event) {
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        if (!form) return;

        if (
            form.matches('[data-products-sort-form]') ||
            form.matches('[data-products-filters-form]') ||
            form.matches('[data-products-mobile-sort-form]') ||
            form.matches('[data-products-page-form]')
        ) {
            event.preventDefault();
            this.submitForm(form);
        }
    }

    /**
     * Automatyczny submit desktopowych filtrów i selecta sortowania.
     */
    onRootChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) {
            return;
        }

        const sortSelect = target.closest('[data-products-sort-select]');
        if (sortSelect) {
            const sortForm = target.closest('[data-products-sort-form]');
            if (sortForm instanceof HTMLFormElement) {
                this.submitForm(sortForm);
            }
            return;
        }

        if (target.matches('[data-products-page-input]')) {
            const pageForm = target.closest('[data-products-page-form]');
            if (pageForm instanceof HTMLFormElement) {
                const max = parseInt(target.getAttribute('max') || '1', 10);
                const min = parseInt(target.getAttribute('min') || '1', 10);
                let value = parseInt(target.value || '1', 10);

                if (Number.isNaN(value)) {
                    value = min;
                }

                value = Math.max(min, Math.min(max, value));
                target.value = String(value);
                this.submitForm(pageForm);
            }
            return;
        }

        if (target.matches('[data-products-list-qty-input]')) {
            this.normalizeQtyInput(target);
            return;
        }

        const filterForm = target.closest('[data-products-filters-form]');
        if (!(filterForm instanceof HTMLFormElement)) {
            return;
        }

        if (this.isMobileViewport()) {
            return;
        }

        if (target.type === 'checkbox' || target.type === 'radio' || target.tagName === 'SELECT') {
            this.submitForm(filterForm);
        }
    }

    /**
     * Blokuje natywny submit/nawigację z Entera w inputach qty listingu.
     */
    onRootKeydown(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || event.key !== 'Enter') {
            return;
        }

        if (target.matches('[data-products-list-qty-input]')) {
            event.preventDefault();
        }
    }

    /**
     * Otwiera wybrany panel mobilny i aktywuje overlay.
     */
    openPanel(type) {
        this.closePanels();

        if (type === 'filters' && this.filtersPanel) {
            this.filtersPanel.classList.remove('-translate-x-full');
            this.filtersPanel.classList.add('translate-x-0');
            this.activePanel = this.filtersPanel;
        }

        if (type === 'sort' && this.sortPanel) {
            this.sortPanel.classList.remove('translate-y-full');
            this.sortPanel.classList.add('translate-y-0');
            this.activePanel = this.sortPanel;
        }

        this.toggleOverlay(true);
    }

    /**
     * Zamyka wszystkie panele mobilne i resetuje overlay.
     */
    closePanels() {
        if (this.filtersPanel) {
            this.filtersPanel.classList.remove('translate-x-0');
            this.filtersPanel.classList.add('-translate-x-full');
        }

        if (this.sortPanel) {
            this.sortPanel.classList.remove('translate-y-0');
            this.sortPanel.classList.add('translate-y-full');
        }

        this.activePanel = null;
        this.toggleOverlay(false);
    }

    /**
     * Przełącza stan tła i blokadę scrolla dla body.
     */
    toggleOverlay(open) {
        if (this.overlay) {
            if (open) {
                this.overlay.classList.remove('opacity-0', 'pointer-events-none');
                this.overlay.classList.add('opacity-100', 'pointer-events-auto');
            } else {
                this.overlay.classList.add('opacity-0', 'pointer-events-none');
                this.overlay.classList.remove('opacity-100', 'pointer-events-auto');
            }
        }

        document.body.classList.toggle('overflow-hidden', open);
    }

    /**
     * Zamyka panel po naciśnięciu Escape.
     */
    onEsc(event) {
        if (event.key !== 'Escape') return;

        if (this.activePanel) {
            this.closePanels();
        }

        this.closeSortDropdown();
        this.closeViewDropdown();
    }

    /**
     * Zamyka dropdown sortowania po kliknięciu poza komponentem.
     */
    onDocumentClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const sortDropdown = this.root.querySelector('[data-products-sort-dropdown]');
        if (sortDropdown && !sortDropdown.contains(target)) {
            this.closeSortDropdown();
        }

        const viewDropdown = this.root.querySelector('[data-products-view-dropdown]');
        if (viewDropdown && !viewDropdown.contains(target)) {
            this.closeViewDropdown();
        }
    }

    /**
     * Ustawia wybrany widok listingu i zapisuje go lokalnie.
     */
    setListingView(view, persist = true) {
        const normalizedView = view === 'list' ? 'list' : 'grid';
        this.root.dataset.productsView = normalizedView;
        document.documentElement.setAttribute(
            'data-products-listing-view',
            normalizedView
        );

        if (this.grid instanceof HTMLElement) {
            this.grid.dataset.productsView = normalizedView;
        }

        if (this.viewDropdown instanceof HTMLElement) {
            const toggle = this.viewDropdown.querySelector('[data-products-view-toggle]');
            const currentIcon = this.viewDropdown.querySelector(
                '[data-products-view-current-icon]'
            );
            const activeOption = this.viewDropdown.querySelector(
                `[data-products-view-option][data-value="${normalizedView}"]`
            );

            if (toggle instanceof HTMLElement) {
                toggle.dataset.view = normalizedView;
            }

            if (activeOption instanceof HTMLElement) {
                const icon = activeOption.getAttribute('data-icon') || '';

                if (currentIcon instanceof HTMLElement) {
                    currentIcon.classList.remove('icon-grid', 'icon-list');
                    if (icon) {
                        currentIcon.classList.add(icon);
                    }
                }
            }

            this.viewDropdown.querySelectorAll('[data-products-view-option]').forEach(option => {
                option.classList.remove('bg-neutral-100');
                if (option.getAttribute('data-value') === normalizedView) {
                    option.classList.add('bg-neutral-100');
                }
            });
        }

        if (persist) {
            try {
                window.localStorage.setItem(this.storageKey, normalizedView);
            } catch (error) {
                // Ignore storage failures and keep the current DOM state only.
            }
        }
    }

    /**
     * Odczytuje ostatnio zapisany widok lub wraca do domyślnej siatki.
     */
    applySavedView() {
        let view = 'grid';

        try {
            const htmlView = document.documentElement.getAttribute(
                'data-products-listing-view'
            );
            if (htmlView === 'list' || htmlView === 'grid') {
                view = htmlView;
            }

            const savedView = window.localStorage.getItem(this.storageKey);
            if (savedView === 'list' || savedView === 'grid') {
                view = savedView;
            }
        } catch (error) {
            view = 'grid';
        }

        this.setListingView(view, false);
    }

    /**
     * Obsługuje nawigację wstecz/dalej przeglądarki.
     */
    onPopState() {
        this.navigate(window.location.href, { pushState: false, scrollTop: false });
    }

    /**
     * Wysyła formularz GET i przełącza listing AJAX-em.
     */
    submitForm(form) {
        const isPageForm = form.matches('[data-products-page-form]');
        const url = new URL(
            isPageForm ? window.location.href : form.action || window.location.href
        );
        const formData = new FormData(form);

        if (isPageForm) {
            url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');
            url.searchParams.delete('paged');
            url.searchParams.delete('sp_listing_ajax');
        } else {
            url.search = '';
        }

        formData.forEach((value, key) => {
            if (typeof value !== 'string') return;
            const cleanValue = value.trim();
            if (!cleanValue.length) return;
            if (isPageForm) {
                url.searchParams.set(key, cleanValue);
                return;
            }

            url.searchParams.append(key, cleanValue);
        });

        this.navigate(url.toString());
    }

    /**
     * Pobiera nowy HTML listingu i podmienia zawartość bez reloadu.
     */
    async navigate(url, options = {}) {
        const opts = {
            pushState: true,
            scrollTop: true,
            ...options,
        };
        const requestUrl = this.buildAjaxListingUrl(url);

        if (this.isLoading) {
            if (this.abortController) {
                this.abortController.abort();
            }
        }

        this.isLoading = true;
        this.toggleLoading(true);
        this.closePanels();
        this.abortController = new AbortController();

        try {
            const response = await fetch(requestUrl, {
                method: 'GET',
                credentials: 'same-origin',
                signal: this.abortController.signal,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                window.location.href = url;
                return;
            }

            const contentType = response.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                const json = await response.json();
                if (json?.success && json?.data?.listing_html) {
                    this.renderListingFromHtml(
                        json.data.listing_html,
                        json.data.title || ''
                    );
                    if (opts.pushState) {
                        window.history.pushState({}, '', url);
                    }
                    if (opts.scrollTop) {
                        this.scrollToListingTop();
                    }
                    return;
                }
                this.renderCardsFromJson(json);
                if (opts.pushState) {
                    window.history.pushState({}, '', url);
                }
                return;
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const nextRoot = doc.querySelector('[data-products-listing]');

            if (!nextRoot) {
                window.location.href = url;
                return;
            }

            this.root.innerHTML = nextRoot.innerHTML;
            this.refreshUiRefs();
            this.applySavedView();
            this.initAccordions();
            this.initPriceRanges();
            initProductCardGalleries();
            this.scheduleProductCartCountsRefresh(0);
            document.title = doc.title || document.title;

            if (opts.pushState) {
                window.history.pushState({}, '', url);
            }

            if (opts.scrollTop) {
                this.scrollToListingTop();
            }
        } catch (error) {
            if (error && error.name === 'AbortError') {
                return;
            }

            window.location.href = url;
        } finally {
            this.isLoading = false;
            this.toggleLoading(false);
        }
    }

    buildAjaxListingUrl(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            parsed.searchParams.set('sp_listing_ajax', '1');
            return parsed.toString();
        } catch (error) {
            return url;
        }
    }

    renderListingFromHtml(listingHtml, title = '') {
        if (!listingHtml) {
            return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(listingHtml, 'text/html');
        const nextRoot = doc.querySelector('[data-products-listing]');
        if (!nextRoot) {
            return;
        }

        this.root.innerHTML = nextRoot.innerHTML;
        this.refreshUiRefs();
        this.applySavedView();
        this.initAccordions();
        this.initPriceRanges();
        initProductCardGalleries();
        this.scheduleProductCartCountsRefresh(0);

        if (title) {
            document.title = title;
        }
    }

    /**
     * Fallback render kart z odpowiedzi JSON.
     */
    renderCardsFromJson(payload) {
        const hits = payload?.data?.hits?.hits || [];
        const grid = this.root.querySelector('[data-products-grid]');
        if (!grid) return;

        grid.innerHTML = '';
        hits.forEach(item => {
            const source = item?._source || {};
            grid.appendChild(createCard(source, 'zł'));
        });
        this.refreshUiRefs();
        this.applySavedView();
        this.scheduleProductCartCountsRefresh(0);
        initProductCardGalleries();
    }

    /**
     * Dodaje lekki stan ładowania podczas pobierania.
     */
    toggleLoading(active) {
        this.root.classList.toggle('pointer-events-none', active);
        this.root.classList.toggle('opacity-70', active);
    }

    /**
     * Sprawdza, czy URL wskazuje ten sam origin.
     */
    isSameOrigin(url) {
        try {
            const current = new URL(window.location.href);
            const parsed = new URL(url, current.origin);
            return parsed.origin === current.origin;
        } catch (error) {
            return false;
        }
    }

    /**
     * Scrolluje listing z offsetem headera, aby wynik nie lądował zbyt nisko.
     */
    scrollToListingTop() {
        const header = document.querySelector(
            '[data-site-header], .site-header, header.site-header, header'
        );
        const headerOffset = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
        const top = this.root.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

        window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth',
        });
    }

    /**
     * Otwiera/zamyka custom dropdown sortowania.
     */
    toggleSortDropdown() {
        this.closeViewDropdown();

        const dropdown = this.root.querySelector('[data-products-sort-dropdown]');
        if (!dropdown) return;

        const menu = dropdown.querySelector('[data-products-sort-menu]');
        const toggle = dropdown.querySelector('[data-products-sort-toggle]');
        const icon = dropdown.querySelector('[data-products-sort-icon]');
        if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
            this.closeSortDropdown();
            return;
        }

        menu.classList.remove('opacity-0', 'pointer-events-none');
        menu.classList.add('opacity-100', 'pointer-events-auto');
        toggle.setAttribute('aria-expanded', 'true');
        if (icon instanceof HTMLElement) {
            icon.classList.add('rotate-180');
        }
    }

    /**
     * Zamyka custom dropdown sortowania.
     */
    closeSortDropdown() {
        const dropdown = this.root.querySelector('[data-products-sort-dropdown]');
        if (!dropdown) return;

        const menu = dropdown.querySelector('[data-products-sort-menu]');
        const toggle = dropdown.querySelector('[data-products-sort-toggle]');
        const icon = dropdown.querySelector('[data-products-sort-icon]');
        if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

        menu.classList.add('opacity-0', 'pointer-events-none');
        menu.classList.remove('opacity-100', 'pointer-events-auto');
        toggle.setAttribute('aria-expanded', 'false');
        if (icon instanceof HTMLElement) {
            icon.classList.remove('rotate-180');
        }
    }

    /**
     * Otwiera/zamyka dropdown wyboru widoku.
     */
    toggleViewDropdown() {
        const dropdown = this.root.querySelector('[data-products-view-dropdown]');
        if (!dropdown) return;

        this.closeSortDropdown();

        const menu = dropdown.querySelector('[data-products-view-menu]');
        const toggle = dropdown.querySelector('[data-products-view-toggle]');
        const icon = dropdown.querySelector('[data-products-view-icon]');
        if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
            this.closeViewDropdown();
            return;
        }

        menu.classList.remove('opacity-0', 'pointer-events-none');
        menu.classList.add('opacity-100', 'pointer-events-auto');
        toggle.setAttribute('aria-expanded', 'true');
        if (icon instanceof HTMLElement) {
            icon.classList.add('rotate-180');
        }
    }

    /**
     * Zamyka dropdown wyboru widoku.
     */
    closeViewDropdown() {
        const dropdown = this.root.querySelector('[data-products-view-dropdown]');
        if (!dropdown) return;

        const menu = dropdown.querySelector('[data-products-view-menu]');
        const toggle = dropdown.querySelector('[data-products-view-toggle]');
        const icon = dropdown.querySelector('[data-products-view-icon]');
        if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

        menu.classList.add('opacity-0', 'pointer-events-none');
        menu.classList.remove('opacity-100', 'pointer-events-auto');
        toggle.setAttribute('aria-expanded', 'false');
        if (icon instanceof HTMLElement) {
            icon.classList.remove('rotate-180');
        }
    }

    /**
     * Ustawia wybraną opcję sortowania i odświeża listing.
     */
    selectSortOption(option) {
        const dropdown = this.root.querySelector('[data-products-sort-dropdown]');
        const form = this.root.querySelector('[data-products-sort-form]');
        if (!dropdown || !(form instanceof HTMLFormElement)) return;

        const input = dropdown.querySelector('[data-products-sort-input]');
        const current = dropdown.querySelector('[data-products-sort-current]');
        if (!(input instanceof HTMLInputElement) || !(current instanceof HTMLElement)) return;

        const value = option.getAttribute('data-value') || 'default';
        const label = option.getAttribute('data-label') || '';

        input.value = value;
        current.textContent = label;

        dropdown.querySelectorAll('[data-products-sort-option]').forEach(el => {
            el.classList.remove('bg-neutral-100');
        });
        option.classList.add('bg-neutral-100');

        this.closeSortDropdown();
        this.submitForm(form);
    }

    /**
     * Ustawia widok listy lub siatki na podstawie wybranej opcji.
     */
    selectViewOption(option) {
        const value = option.getAttribute('data-value') || 'grid';
        this.setListingView(value);
        this.closeViewDropdown();
    }

    /**
     * Inicjalizuje suwaki zakresu ceny.
     */
    initPriceRanges() {
        const ranges = this.root.querySelectorAll('[data-price-range]');
        ranges.forEach(range => this.setupPriceRange(range));
    }

    /**
     * Ustawia stan początkowy accordionów i ikon strzałek.
     */
    initAccordions() {
        const accordions = this.root.querySelectorAll('[data-filter-accordion]');
        accordions.forEach(accordion => {
            const content = accordion.querySelector('[data-filter-accordion-content]');
            const icon = accordion.querySelector('[data-filter-accordion-icon]');
            if (!(content instanceof HTMLElement)) return;

            if (accordion.hasAttribute('open')) {
                content.style.height = 'auto';
                if (icon instanceof HTMLElement) {
                    icon.classList.add('rotate-180');
                }
            } else {
                content.style.height = '0px';
                if (icon instanceof HTMLElement) {
                    icon.classList.remove('rotate-180');
                }
            }
        });
    }

    /**
     * Płynnie otwiera lub zamyka pojedynczy accordion filtra.
     */
    toggleAccordion(accordion) {
        if (!(accordion instanceof HTMLElement)) return;

        const content = accordion.querySelector('[data-filter-accordion-content]');
        const inner = accordion.querySelector('[data-filter-accordion-inner]');
        const icon = accordion.querySelector('[data-filter-accordion-icon]');
        if (!(content instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;

        const isOpen = accordion.hasAttribute('open');
        content.style.overflow = 'hidden';
        content.style.transition = 'height 240ms ease';

        if (!isOpen) {
            accordion.setAttribute('open', '');
            content.style.height = '0px';
            requestAnimationFrame(() => {
                content.style.height = `${inner.scrollHeight}px`;
            });

            const onOpenEnd = () => {
                content.style.height = 'auto';
                content.style.overflow = '';
                content.style.transition = '';
                content.removeEventListener('transitionend', onOpenEnd);
            };

            content.addEventListener('transitionend', onOpenEnd);
            if (icon instanceof HTMLElement) {
                icon.classList.add('rotate-180');
            }
            return;
        }

        content.style.height = `${inner.scrollHeight}px`;
        requestAnimationFrame(() => {
            content.style.height = '0px';
        });

        const onCloseEnd = () => {
            accordion.removeAttribute('open');
            content.style.overflow = '';
            content.style.transition = '';
            content.removeEventListener('transitionend', onCloseEnd);
        };

        content.addEventListener('transitionend', onCloseEnd);
        if (icon instanceof HTMLElement) {
            icon.classList.remove('rotate-180');
        }
    }

    /**
     * Spina slider z polami hidden i etykietami od/do.
     */
    setupPriceRange(rangeElement) {
        if (!(rangeElement instanceof HTMLElement)) return;

        const min = parseInt(rangeElement.dataset.min || '0', 10);
        const max = parseInt(rangeElement.dataset.max || '0', 10);
        const step = parseInt(rangeElement.dataset.step || '1', 10);

        const minRange = rangeElement.querySelector('[data-price-range-min-range]');
        const maxRange = rangeElement.querySelector('[data-price-range-max-range]');
        const progress = rangeElement.querySelector('[data-price-range-progress]');
        const minLabel = rangeElement.querySelector('[data-price-range-min-label]');
        const maxLabel = rangeElement.querySelector('[data-price-range-max-label]');
        const minInput = rangeElement.querySelector('[data-price-range-min-input]');
        const maxInput = rangeElement.querySelector('[data-price-range-max-input]');

        if (
            !(minRange instanceof HTMLInputElement) ||
            !(maxRange instanceof HTMLInputElement) ||
            !(progress instanceof HTMLElement) ||
            !(minLabel instanceof HTMLElement) ||
            !(maxLabel instanceof HTMLElement) ||
            !(minInput instanceof HTMLInputElement) ||
            !(maxInput instanceof HTMLInputElement)
        ) {
            return;
        }

        const form = rangeElement.closest('[data-products-filters-form]');

        const update = (source = 'min') => {
            let minValue = parseInt(minRange.value || String(min), 10);
            let maxValue = parseInt(maxRange.value || String(max), 10);

            if (Number.isNaN(minValue)) minValue = min;
            if (Number.isNaN(maxValue)) maxValue = max;

            if (source === 'min' && minValue >= maxValue) {
                minValue = maxValue - step;
            }
            if (source === 'max' && maxValue <= minValue) {
                maxValue = minValue + step;
            }

            if (minValue < min) minValue = min;
            if (maxValue > max) maxValue = max;

            minRange.value = String(minValue);
            maxRange.value = String(maxValue);

            minLabel.textContent = String(minValue);
            maxLabel.textContent = String(maxValue);

            minInput.value = minValue > min ? String(minValue) : '';
            maxInput.value = maxValue < max ? String(maxValue) : '';

            const range = max - min || 1;
            const minPercent = ((minValue - min) / range) * 100;
            const maxPercent = ((maxValue - min) / range) * 100;

            progress.style.left = `${minPercent}%`;
            progress.style.width = `${maxPercent - minPercent}%`;
        };

        minRange.addEventListener('input', () => update('min'));
        maxRange.addEventListener('input', () => update('max'));

        const submitOnDesktop = () => {
            if (this.isMobileViewport()) return;
            if (form instanceof HTMLFormElement) {
                this.submitForm(form);
            }
        };

        minRange.addEventListener('change', () => {
            update('min');
            submitOnDesktop();
        });
        maxRange.addEventListener('change', () => {
            update('max');
            submitOnDesktop();
        });

        update();
    }

    /**
     * Zmienia wartość steppera qty w widoku listy.
     */
    handleQtyStep(button) {
        const wrapper = button.closest('[data-products-list-qty]');
        const input = wrapper?.querySelector('[data-products-list-qty-input]');
        if (!(input instanceof HTMLInputElement)) {
            return;
        }

        const step = parseInt(input.step || '1', 10) || 1;
        const min = parseInt(input.min || '1', 10) || 1;
        const max = input.max ? parseInt(input.max, 10) : null;
        const current = parseInt(input.value || String(min), 10) || min;
        const delta =
            button.getAttribute('data-products-list-qty-step') === 'up' ? step : -step;

        let nextValue = current + delta;
        if (nextValue < min) {
            nextValue = min;
        }
        if (typeof max === 'number' && !Number.isNaN(max) && nextValue > max) {
            nextValue = max;
        }

        input.value = String(nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Normalizuje ręcznie wpisaną ilość do zakresu inputa.
     */
    normalizeQtyInput(input) {
        const min = parseInt(input.min || '1', 10) || 1;
        const max = input.max ? parseInt(input.max, 10) : null;
        let value = parseInt(input.value || String(min), 10);

        if (Number.isNaN(value) || value < min) {
            value = min;
        }
        if (typeof max === 'number' && !Number.isNaN(max) && value > max) {
            value = max;
        }

        input.value = String(value);
    }

    /**
     * Podmienia data-quantity przycisku koszyka zgodnie z qty z widoku listy.
     */
    syncAddToCartQuantity(button) {
        if (!(button instanceof HTMLElement)) {
            return;
        }

        const wrapper = button.closest('[data-product-card]');
        const input = wrapper?.querySelector('[data-products-list-qty-input]');
        if (!(input instanceof HTMLInputElement)) {
            return;
        }

        this.normalizeQtyInput(input);
        button.setAttribute('data-quantity', input.value || '1');
    }

    /**
     * Zwraca true dla widoku mobilnego (poniżej desktop breakpointu).
     */
    isMobileViewport() {
        return window.matchMedia('(max-width: 1023px)').matches;
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const listing = new ProductsListing();
        listing.init();
    });
} else {
    const listing = new ProductsListing();
    listing.init();
}
